import type { Language } from '../types';

type Block = Record<string, string>;

/**
 * Dictionaries are per-language chunks: a visitor downloads the language they read, not all
 * three. Loading is async, `_t` stays synchronous — a missing dictionary renders empty and
 * the app repaints on I18N_LOADED_EVENT once the chunk lands.
 */
const loaders: Record<ShopContentLanguage, () => Promise<Block>> = {
  ru: () => import('../locales/ru').then((m) => m.ru),
  en: () => import('../locales/en').then((m) => m.en),
  he: () => import('../locales/he').then((m) => m.he),
  uk: () => import('../locales/uk').then((m) => m.uk),
  ka: () => import('../locales/ka').then((m) => m.ka),
};

const dictionaries: Partial<Record<ShopContentLanguage, Block>> = {};
const pending: Partial<Record<ShopContentLanguage, Promise<void>>> = {};

export const I18N_LOADED_EVENT = 'catalogofworks:i18n-loaded';
/** Fired when the preferred UI language is persisted (storefront, visiting card, …). */
export const PREFERRED_LANGUAGE_CHANGED_EVENT = 'catalogofworks:preferred-language-changed';

export function ensureLanguage(lang: ShopContentLanguage): Promise<void> {
  if (dictionaries[lang]) return Promise.resolve();
  const inFlight = pending[lang];
  if (inFlight) return inFlight;
  const task = loaders[lang]()
    .then((dict) => {
      dictionaries[lang] = dict;
      delete pending[lang];
      window.dispatchEvent(new CustomEvent(I18N_LOADED_EVENT));
    })
    .catch(() => {
      delete pending[lang];
    });
  pending[lang] = task;
  return task;
}

let currentLang: Language = 'ru';

export function setI18nLanguage(lang: Language) {
  currentLang = lang;
  void ensureLanguage(lang);
}

/** Persist UI language (shared key for storefront + visiting card). */
export function persistPreferredLanguage(lang: Language) {
  if (!['ru', 'en', 'he'].includes(lang)) return;
  const prev = typeof localStorage !== 'undefined'
    ? localStorage.getItem('catalogofworks-lang')
    : null;
  localStorage.setItem('catalogofworks-lang', lang);
  setI18nLanguage(lang);
  if (prev !== lang) {
    window.dispatchEvent(
      new CustomEvent(PREFERRED_LANGUAGE_CHANGED_EVENT, { detail: { lang } }),
    );
  }
}

export function getI18nLanguage(): Language {
  return currentLang;
}

/** Visitor languages. ru/en/he stay the admin languages. uk/ka are the shop plugin. */
export type ShopContentLanguage = Language | 'uk' | 'ka';

export const SHOP_CONTENT_LANGUAGE_EVENT = 'catalogofworks:shop-content-language';

const SHOP_CONTENT_LANG_KEY = 'catalogofworks-shop-lang';

export function isShopContentLanguage(value: unknown): value is ShopContentLanguage {
  return value === 'ru' || value === 'en' || value === 'he' || value === 'uk' || value === 'ka';
}

/**
 * Holyland remembers ua/ge apart from the app language key.
 * The app key only accepts ru, en, and he, and a page load would otherwise
 * write Russian over a saved Ukrainian choice.
 */
export function readHolylandContentLanguage(pluginOn: boolean, uiLang: Language): ShopContentLanguage {
  if (!pluginOn) return uiLang;
  try {
    const savedShop = localStorage.getItem(SHOP_CONTENT_LANG_KEY);
    if (isShopContentLanguage(savedShop)) return savedShop;
    const savedApp = localStorage.getItem('catalogofworks-lang');
    if (savedApp === 'ru' || savedApp === 'en' || savedApp === 'he') return savedApp;
  } catch {
    /* private mode */
  }
  const sys = (typeof navigator !== 'undefined' ? navigator.language : '').split('-')[0].toLowerCase();
  if (sys === 'uk' || sys === 'ka') return sys;
  return uiLang;
}

export function persistHolylandContentLanguage(lang: ShopContentLanguage) {
  try {
    localStorage.setItem(SHOP_CONTENT_LANG_KEY, lang);
  } catch {
    /* private mode */
  }
  if (lang === 'uk' || lang === 'ka') void ensureLanguage(lang);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SHOP_CONTENT_LANGUAGE_EVENT, { detail: { lang } }));
  }
}

export function pickI18nText(
  block: Partial<Record<Language, string>> | Record<string, string | undefined> | null | undefined,
  lang: string,
): string {
  if (!block) return '';
  const bag = block as Record<string, string | undefined>;
  return (
    bag[lang]?.trim() ||
    bag.ru?.trim() ||
    bag.en?.trim() ||
    bag.he?.trim() ||
    ''
  );
}

export function _t(path: string, lang?: ShopContentLanguage): string {
  const targetLang = lang || currentLang;
  const dict = dictionaries[targetLang];
  if (!dict) {
    // Empty beats a key name flashing on screen; the repaint follows in milliseconds.
    void ensureLanguage(targetLang);
    return '';
  }
  const value = dict[path];
  if (value) return value;
  return `${targetLang}.${path}`;
}

export function languageDirection(lang: ShopContentLanguage): 'ltr' | 'rtl' {
  return lang === 'he' ? 'rtl' : 'ltr';
}

export function isUiLanguage(value: unknown): value is Language {
  return value === 'ru' || value === 'en' || value === 'he';
}

/** Platform → storefront handoff: `?lang=` overrides saved preference for this visit. */
export function readLanguageFromSearch(search = typeof window !== 'undefined' ? window.location.search : ''): Language | null {
  const raw = new URLSearchParams(search).get('lang');
  return isUiLanguage(raw) ? raw : null;
}

export function detectInitialLanguage(): Language {
  // 1) Explicit handoff from platform / deep link, or a language address such as /en
  const fromPath = typeof window !== 'undefined'
    ? /^\/(ru|en|he)(?:\/|$)/.exec(window.location.pathname)?.[1]
    : null;
  if (isUiLanguage(fromPath ?? null)) return fromPath as Language;
  const fromUrl = readLanguageFromSearch();
  if (fromUrl) return fromUrl;

  // 2) Last choice on this origin
  const saved = localStorage.getItem('catalogofworks-lang') as Language | null;
  if (isUiLanguage(saved)) return saved;

  // 3) Browser / system
  const sys = navigator.language.split('-')[0];
  if (sys === 'he') return 'he';
  if (sys === 'en') return 'en';
  return 'ru';
}
